import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Paper,
  Chip,
  Avatar,
  Toolbar,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  Circle as CircleIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  DoneAll as DoneAllIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  ExitToApp as ExitToAppIcon,
  LockOpen as LockOpenIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import MessageBubble from '../../MessageBubble';
import AdminMessageInput from '../../AdminMessageInput';
import { Message, Customer } from '../../../types';
import apiClient from '../../../utils/api';
import socketClient from '../../../utils/socket';

interface ChatAreaProps {
  selectedChatId: string | null;
  selectedCustomer: Customer | null;
  roomTitle?: string;
  initialRoomStatus?: string;
  euLoggedIn?: boolean;
  onRoomStatusChanged?: (newStatus: string) => void;
  onBackToList?: () => void;
  onShowCustomerProfile?: () => void;
  showBackButton?: boolean;
  showCustomerButton?: boolean;
  onRoomEnter?: () => void;
  onRoomLeave?: () => void;
}

export const ChatArea: React.FC<ChatAreaProps> = ({
  selectedChatId,
  selectedCustomer,
  roomTitle,
  initialRoomStatus,
  euLoggedIn,
  onRoomStatusChanged,
  onBackToList,
  onShowCustomerProfile,
  showBackButton = false,
  showCustomerButton = false,
  onRoomEnter,
  onRoomLeave,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);

  // RESTポーリングに切替
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false); // 入力フォーカス状態を追跡
  const [roomStatus, setRoomStatus] = useState<string>(''); // ルームステータス（未割当: 空文字）
  const [roomStatusDisplay, setRoomStatusDisplay] = useState<string>('未割当'); // ルームステータス表示
  const [inputHeight, setInputHeight] = useState<number>(120); // 入力エリアの実高さ
  const [connected, setConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [paging, setPaging] = useState<{ nextBefore?: string | null; hasMoreOlder?: boolean }>({});
  const [isInRoom, setIsInRoom] = useState(false); // 入室状態
  const isAcquiringLockRef = useRef(false); // ロック取得中フラグ
  
  // ポーリング制御用のRef（useEffect依存配列に含めない）
  const isInputFocusedRef = useRef(isInputFocused);
  const connectedRef = useRef(connected);
  
  // Refを常に最新に保つ
  useEffect(() => {
    isInputFocusedRef.current = isInputFocused;
  }, [isInputFocused]);
  
  useEffect(() => {
    connectedRef.current = connected;
  }, [connected]);
  const sendTyping = () => {
    if (selectedChatId) socketClient.startTyping(selectedChatId);
  };
  const markAsRead = () => {};
  const hasUnread = false;

  // WebSocket接続・購読・ロック管理
  useEffect(() => {
    if (!selectedChatId) return;
    
    socketClient.connect_socket();
    
    // ルーム入室時にロック取得を試みる
    const acquireRoomLock = async () => {
      isAcquiringLockRef.current = true;
      setIsInRoom(true);
      socketClient.joinRoom(selectedChatId);
      if (onRoomEnter) onRoomEnter();
      
      try {
        const socketId = (window as any).CSCHAT_SOCKET_ID || '';
        const result = await apiClient.acquireLock(selectedChatId, socketId);
        
        console.log('🔓 ロック取得結果:', result);
        
        if (result.success === false && result.lockedBy) {
          const lockedByName = result.lockedBy?.username || '他の担当者';
          setError(`このチャットルームは${lockedByName}が閲覧中です（閲覧のみ）`);
          setIsInRoom(false);
          console.warn('🔒 ロック取得失敗（閲覧モード）:', result.message);
        } else {
          setError(null);
        }
      } catch (error: any) {
        if (error?.response?.status === 423) {
          const lockedByName = error?.response?.data?.lockedBy?.username || '他の担当者';
          const proceed = window.confirm(
            `このチャットルームは${lockedByName}が利用中です。\n閲覧のみモードで開きますか？`
          );
          if (!proceed) {
            socketClient.leaveRoom(selectedChatId);
            setIsInRoom(false);
            if (onRoomLeave) onRoomLeave();
            return;
          }
          setError(`このチャットルームは${lockedByName}が閲覧中です（閲覧のみ）`);
          setIsInRoom(false);
          console.warn('🔒 ルームはロック中（閲覧モード）:', error?.response?.data);
        } else {
          console.error('❌ ロック取得エラー（入力は可能）:', error);
          setError(null);
        }
      } finally {
        isAcquiringLockRef.current = false;
      }
    };
    
    acquireRoomLock();
    
    const onConn = () => setConnected(true);
    const onDisc = () => setConnected(false);
    const onLockUpdate = (e: any) => {
      const { roomId, locked, owner } = (e as CustomEvent).detail || {};
      if (roomId === selectedChatId && locked && owner && owner !== (window as any).CSCHAT_SOCKET_ID) {
        // ロック取得処理中は重複アラートを抑止
        if (isAcquiringLockRef.current) return;
        socketClient.leaveRoom(selectedChatId);
        alert('このチャットルームは他の担当者が閲覧中のため、読み取り専用になりました。');
      }
    };
    const onNew = (e: any) => {
      const msg = (e as CustomEvent).detail as Message;
      if (msg.chat_room_id !== selectedChatId) return;
      // 重複ガード: 既に同一IDのメッセージが存在する場合は追加しない
      setMessages(prev => {
        if (prev.some(m => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      setTimeout(scrollToBottom, 50);
    };
    const onTyping = (e: any) => {
      const data = (e as CustomEvent).detail as { roomId: string; username: string; isTyping: boolean };
      if (data.roomId !== selectedChatId) return;
      setTypingUsers(prev => {
        const set = new Set(prev);
        if (data.isTyping) set.add(data.username || '相手'); else set.delete(data.username || '相手');
        return Array.from(set);
      });
    };
    const onForceKicked = (e: any) => {
      const { roomId, kickedBy } = (e as CustomEvent).detail || {};
      if (roomId !== selectedChatId) return;
      socketClient.leaveRoom(selectedChatId);
      setIsInRoom(false);
      setError(`${kickedBy || '他のユーザー'}により強制退室されました（閲覧のみ）`);
      alert(`${kickedBy || '他のユーザー'}により強制退室されました。`);
    };
    window.addEventListener('rt:connected', onConn);
    window.addEventListener('rt:disconnected', onDisc);
    window.addEventListener('rt:newMessage', onNew as EventListener);
    window.addEventListener('rt:roomLockUpdate', onLockUpdate as EventListener);
    window.addEventListener('rt:userTyping', onTyping as EventListener);
    window.addEventListener('rt:forceKicked', onForceKicked as EventListener);
    
    return () => {
      socketClient.leaveRoom(selectedChatId);
      apiClient.releaseLock(selectedChatId).catch(err => {
        console.error('❌ ロック解放エラー:', err);
      });
      
      window.removeEventListener('rt:connected', onConn);
      window.removeEventListener('rt:disconnected', onDisc);
      window.removeEventListener('rt:newMessage', onNew as EventListener);
      window.removeEventListener('rt:roomLockUpdate', onLockUpdate as EventListener);
      window.removeEventListener('rt:userTyping', onTyping as EventListener);
      window.removeEventListener('rt:forceKicked', onForceKicked as EventListener);
    };
  }, [selectedChatId]); // 依存配列からisInRoom, onRoomEnter, onRoomLeaveを削除

  // ルーム入室時に未読を全既読にする
  useEffect(() => {
    const markAll = async () => {
      if (!selectedChatId) return;
      try {
        const resp = await apiClient.markAllMessagesAsRead(selectedChatId);
        if (resp.success) {
          setMessages(prev => prev.map(m => ({ ...m, is_read: true } as Message)));
          // 左カラムの未読バッジ更新
          window.dispatchEvent(new CustomEvent('rt:roomAllRead', { detail: { roomId: selectedChatId } }));
        }
      } catch {}
    };
    markAll();
  }, [selectedChatId]);

  // 選択変更時に親から受け取ったステータスで初期化
  useEffect(() => {
    if (initialRoomStatus !== undefined) {
      setRoomStatus(initialRoomStatus || '');
      setRoomStatusDisplay(getStatusDisplayName(initialRoomStatus || ''));
    }
  }, [selectedChatId, initialRoomStatus]);

  // 現在時刻の表示用
  const [currentTime, setCurrentTime] = useState(new Date());

  // 1分ごとに時刻を更新
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // メッセージ送信（REST）
  const handleSendMessage = async (content: string, attachments?: File[]) => {
    console.log('🚀 handleSendMessage 開始:', { selectedChatId, content: content.trim() });
    
    if (!selectedChatId || (!content.trim() && !(attachments && attachments.length > 0))) {
      console.log('❌ 送信条件不足:', { selectedChatId, hasContent: !!content.trim() });
      return;
    }
    
    try {
      // 添付があれば先にアップロード
      let fileMeta: { url: string; filename: string; originalName?: string } | undefined;
      if (attachments && attachments.length > 0) {
        const file = attachments[0];
        console.log('📎 ファイルアップロード開始:', file.name, file.size);
        const uploaded = await apiClient.uploadFile(file);
        console.log('📎 アップロードレスポンス:', uploaded);
        if ((uploaded as any).success) {
          fileMeta = { 
            url: (uploaded as any).url, 
            filename: (uploaded as any).fileName || file.name,
            originalName: (uploaded as any).originalName || file.name
          };
          console.log('✅ fileMeta設定完了:', fileMeta);
        } else {
          console.error('❌ アップロード失敗:', uploaded);
        }
      }
      // ログインしている管理者名を送信者名に反映
      let senderName = '管理者';
      try {
        const adminUserStr = localStorage.getItem('adminUser');
        if (adminUserStr) {
          const adminUser = JSON.parse(adminUserStr || '{}');
          if (adminUser?.displayName) senderName = adminUser.displayName;
        }
      } catch {}
      console.log('📤 API送信開始:', { 
        chatId: selectedChatId, 
        content: content.trim(),
        fileMeta: fileMeta,
        messageType: fileMeta ? 'file' : 'text',
        file_url: fileMeta?.url,
        file_name: fileMeta?.originalName || fileMeta?.filename
      });
      const result = await apiClient.sendMessage(selectedChatId, {
        content: content.trim(),
        sender_type: 'admin',
        sender_name: senderName,
        message_type: fileMeta ? 'file' : 'text',
        file_url: fileMeta?.url,
        file_name: fileMeta?.originalName || fileMeta?.filename,
      });
      
      console.log('📦 API送信結果:', result);
      
      if (result.success && result.data) {
        console.log('✅ 送信成功 - メッセージ一覧を再取得中...');
        // 送信成功後、最新のメッセージ一覧を再取得（ポーリングと干渉しないように）
        const refreshResponse = await apiClient.getMessages(selectedChatId, { limit: 5000 });
        console.log('📋 メッセージ一覧取得結果:', refreshResponse);
        // 重複ガード: IDでユニーク化
        const list = (refreshResponse.messages || []);
        const uniqueById: Record<string, Message> = {} as any;
        for (const m of list) uniqueById[m.id] = m;
        setMessages(Object.values(uniqueById));
        setError(null);
        scrollToBottom();
      } else {
        console.log('❌ 送信失敗:', result.message);
        setError(result.message || 'メッセージ送信に失敗しました');
      }
    } catch (e: any) {
      console.error('💥 送信エラー:', e?.response?.data || e);
      const serverMsg = e?.response?.data?.message;
      setError(serverMsg || 'メッセージ送信中にエラーが発生しました');
    }
  };

  // メッセージ編集
  const handleEditMessage = async (messageId: string, newContent: string) => {
    if (!selectedChatId) return;
    try {
      const response = await apiClient.editMessage(selectedChatId, messageId, newContent);
      if (response.success && response.data) {
        setMessages(prev => prev.map(m => m.id === messageId ? { ...m, content: newContent, is_edited: true } as Message : m));
        setError(null);
      } else {
        setError(response.message || 'メッセージの編集に失敗しました');
      }
    } catch (error) {
      console.error('メッセージ編集エラー:', error);
      setError('メッセージの編集に失敗しました');
    }
  };

  // ルーム完了
  const handleCompleteRoom = async () => {
    if (!selectedChatId) return;
    
    const reason = window.prompt('完了理由を入力してください（任意）:');
    if (reason === null) return; // キャンセル
    
    try {
      const response = await apiClient.completeRoom(selectedChatId, reason || undefined);
      if (response?.success) {
        console.log('✅ ルーム完了成功');
        setRoomStatus('completed');
        setRoomStatusDisplay('完了');
        // 親へ通知してリストも同期
        onRoomStatusChanged?.('completed');
        // メッセージ再取得でシステムメッセージも表示
        try {
          const refreshResponse = await apiClient.getMessages(selectedChatId, { limit: 5000 });
          setMessages(refreshResponse.messages || []);
        } catch {}
      }
    } catch (error) {
      console.error('💥 ルーム完了エラー:', error);
      alert('ルームの完了に失敗しました');
    }
  };

  // ルーム再開
  const handleReopenRoom = async () => {
    if (!selectedChatId) return;
    
    const reason = window.prompt('再開理由を入力してください（任意）:');
    if (reason === null) return; // キャンセル
    
    try {
      const response = await apiClient.reopenRoom(selectedChatId, reason || undefined);
      if (response?.success) {
        console.log('✅ ルーム再開成功');
        setRoomStatus('active');
        setRoomStatusDisplay('対応中');
        onRoomStatusChanged?.('active');
        try {
          const refreshResponse = await apiClient.getMessages(selectedChatId, { limit: 5000 });
          setMessages(refreshResponse.messages || []);
        } catch {}
      }
    } catch (error) {
      console.error('💥 ルーム再開エラー:', error);
      alert('ルームの再開に失敗しました');
    }
  };

  // ステータス変更ハンドラー
  const handleStatusChange = async (event: SelectChangeEvent<string>) => {
    if (!selectedChatId) return;
    
    const newStatus = event.target.value;
    
    // 削除のみ確認ダイアログを表示
    if (newStatus === 'archived') {
      const reason = window.prompt(`ステータスを「削除」に変更します。理由を入力してください（任意）:`);
      if (reason === null) return; // キャンセル
      
      try {
        const response = await apiClient.updateRoomStatus(selectedChatId, newStatus, reason || undefined);
        if (response?.success) {
          console.log('✅ ステータス変更成功:', newStatus);
          setRoomStatus(newStatus);
          setRoomStatusDisplay(getStatusDisplayName(newStatus));
          onRoomStatusChanged?.(newStatus);
          try {
            const refreshResponse = await apiClient.getMessages(selectedChatId, { limit: 5000 });
            setMessages(refreshResponse.messages || []);
          } catch {}
        }
      } catch (error) {
        console.error('💥 ステータス変更エラー:', error);
        alert('ステータスの変更に失敗しました');
      }
    } else {
      // 削除以外は即座に変更
      try {
        const response = await apiClient.updateRoomStatus(selectedChatId, newStatus);
        if (response?.success) {
          console.log('✅ ステータス変更成功:', newStatus);
          setRoomStatus(newStatus);
          setRoomStatusDisplay(getStatusDisplayName(newStatus));
          onRoomStatusChanged?.(newStatus);
          try {
            const refreshResponse = await apiClient.getMessages(selectedChatId, { limit: 5000 });
            setMessages(refreshResponse.messages || []);
          } catch {}
        }
      } catch (error) {
        console.error('💥 ステータス変更エラー:', error);
        alert('ステータスの変更に失敗しました');
      }
    }
  };

  // ステータス表示名を取得
  const getStatusDisplayName = (status: string): string => {
    const statusMap: Record<string, string> = {
      'pending': '未対応',
      'active': '対応中',
      'waiting': '返信待ち',
      'completed': '完了',
      'temp_completed': '一時完了',
      'paused': '保留',
      'archived': '削除',
    };
    if (!status) return '未割当';
    return statusMap[status] || status;
  };

  // メッセージ削除
  const handleDeleteMessage = async (messageId: string) => {
    if (!selectedChatId) return;
    try {
      const response = await apiClient.deleteMessage(selectedChatId, messageId);
      if ((response as any).success !== false) {
        setMessages(prev => prev.filter(m => m.id !== messageId));
        setError(null);
      } else {
        setError((response as any).message || 'メッセージの削除に失敗しました');
      }
    } catch (error) {
      console.error('メッセージ削除エラー:', error);
      setError('メッセージの削除に失敗しました');
    }
  };

  // 強制ロック解除ハンドラー
  const handleForceReleaseLock = async () => {
    if (!selectedChatId) return;

    const confirmed = window.confirm(
      '現在利用しているユーザーを強制的に退室させますがよろしいでしょうか。'
    );
    if (!confirmed) return;

    try {
      const result = await apiClient.forceReleaseLock(selectedChatId);
      if (result.success) {
        // 強制解除後、自分がロックを取得
        const socketId = (window as any).CSCHAT_SOCKET_ID || '';
        await apiClient.acquireLock(selectedChatId, socketId);
        setIsInRoom(true);
        setError(null);
      }
    } catch (error) {
      console.error('強制ロック解除エラー:', error);
      alert('強制ロック解除に失敗しました');
    }
  };

  // 退室ハンドラー（入室中・閲覧中どちらでも実行可能）
  const handleLeaveRoom = async () => {
    if (!selectedChatId) return;
    
    try {
      // ロック解放（入室中の場合のみ）
      if (isInRoom) {
        await apiClient.releaseLock(selectedChatId);
      }
      socketClient.leaveRoom(selectedChatId);
      
      // 状態をクリア
      setIsInRoom(false);
      if (onRoomLeave) onRoomLeave(); // 親コンポーネントに通知
      
      console.log('🚪 退室完了:', selectedChatId);
    } catch (error) {
      console.error('❌ 退室エラー:', error);
      // エラーでも親コンポーネントに通知（リストに戻る）
      if (onRoomLeave) onRoomLeave();
    }
  };

  // 自動スクロール（常に最新メッセージを表示）
  const scrollToBottom = () => {
    const perform = (behavior: ScrollBehavior = 'auto') => {
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior });
        return;
      }
      messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
    };
    requestAnimationFrame(() => perform('auto'));
    setTimeout(() => perform('auto'), 50);
  };

  // タイピングハンドラー
  const handleTyping = (isTyping: boolean) => {
    sendTyping(); // WebSocket無効化により引数不要
  };

  // メッセージが更新された時に自動スクロール
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 上端付近で過去メッセージを読み込む（反転無限スクロール）
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el || !selectedChatId) return;
    let loadingOlder = false;
    const onScroll = async () => {
      if (loadingOlder) return;
      if (el.scrollTop <= 200 && paging?.hasMoreOlder && paging?.nextBefore) {
        loadingOlder = true;
        const prevHeight = el.scrollHeight;
        try {
          const resp = await apiClient.getMessages(selectedChatId, { before: paging.nextBefore, limit: 5000 });
          const older = resp.messages || [];
          if (older.length > 0) {
            setMessages(prev => [...older, ...prev]);
            const p: any = (resp as any).pagination;
            if (p) setPaging({ nextBefore: p.nextBefore, hasMoreOlder: p.hasMore });
            setTimeout(() => {
              const newHeight = el.scrollHeight;
              el.scrollTop = newHeight - prevHeight + el.scrollTop;
            }, 0);
          } else {
            setPaging(prev => ({ ...prev, hasMoreOlder: false }));
          }
        } finally {
          loadingOlder = false;
        }
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [selectedChatId, paging?.hasMoreOlder, paging?.nextBefore]);

  // 入力エリアの高さを自動計測し、最下部の被りを防止
  useEffect(() => {
    const measure = () => {
      const h = inputContainerRef.current?.getBoundingClientRect().height || 120;
      setInputHeight(h);
    };

    measure();

    // ResizeObserverで動的に追従
    const target = inputContainerRef.current;
    if (target && 'ResizeObserver' in window) {
      const ro = new ResizeObserver(() => {
        measure();
        // 高さ変化時も下端を維持
        scrollToBottom();
      });
      ro.observe(target);
      return () => ro.disconnect();
    }

    // フォールバック: リサイズで再計測
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // 初回＋45秒毎にメッセージ取得（入力中は停止、ソケット接続時も停止）
  useEffect(() => {
    if (!selectedChatId) return;
    let cancelled = false;
    let intervalId: NodeJS.Timeout | null = null;
    
    const fetchMessages = async () => {
      try {
        if (!cancelled) setLoading(true);
        const response = await apiClient.getMessages(selectedChatId, { limit: 5000 });
        if (!cancelled) {
          setMessages(response.messages || []);
          setError(null);
          // 最新のメッセージが見えるようにスクロール
          scrollToBottom();
          const p: any = (response as any).pagination;
          if (p) {
            setPaging({ nextBefore: p.nextBefore, hasMoreOlder: p.hasMore });
          }
        }
      } catch (e: any) {
        // 認証エラーの場合はログイン画面にリダイレクトされるので、ここではエラーをログに出すのみ
        if (!cancelled && (!e.response || e.response.status !== 401)) {
          setError('メッセージの取得に失敗しました');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    
    // 初回読み込み
    fetchMessages();
    
    // 条件付きポーリング: 入力中でなく、ソケット未接続時のみ45秒間隔
    const computeIntervalMs = () => {
      const base = 45000; // 45秒
      const inactiveBoost = document.visibilityState !== 'visible' ? 30000 : 0; // 非表示なら+30秒
      return base + inactiveBoost;
    };
    
    const startPolling = () => {
      // Refの値を参照してポーリング開始判定
      if (!isInputFocusedRef.current && !connectedRef.current) {
        if (intervalId) clearInterval(intervalId);
        intervalId = setInterval(fetchMessages, computeIntervalMs());
      } else {
      }
    };
    
    startPolling();

    const onVisibility = () => {
      if (intervalId) clearInterval(intervalId);
      startPolling();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => { 
      cancelled = true; 
      if (intervalId) {
        clearInterval(intervalId);
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [selectedChatId]); // 依存配列からisInputFocusedとconnectedを削除

  // チャット未選択時の表示
  if (!selectedChatId) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'grey.50',
        }}
      >
        <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '1rem' }}>
            チャットを選択してください
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
            左のサイドバーからチャットを選択すると、こちらにメッセージが表示されます
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      position: 'relative' 
    }}>
      {/* チャットヘッダー */}
      <Paper elevation={0} sx={{ zIndex: 1, border: 'none' }}>
        <Toolbar variant="dense" sx={{ minHeight: 56, px: 2 }}>
          {/* 戻るボタン（モバイル） */}
          {showBackButton && (
            <IconButton
              edge="start"
              onClick={onBackToList}
              sx={{ mr: 1 }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}

          {/* 顧客情報 */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Avatar sx={{ width: 32, height: 32, mr: 1.5 }}>
              {selectedCustomer?.name?.charAt(0) || roomTitle?.charAt(0) || 'C'}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              {/* ルーム名（メイン） */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {roomTitle || selectedCustomer?.name || 'お客様'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* 顧客名 */}
                <Typography variant="caption" color="text.secondary">
                  {selectedCustomer?.name || 'お客様'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedCustomer?.email || 'メール未登録'}
                </Typography>
                {selectedCustomer?.membershipLevel === 'vip' && (
                  <Chip label="VIP" size="small" color="warning" sx={{ height: 16, fontSize: '0.6rem' }} />
                )}
                {/* ルームステータス */}
                <Chip
                  label={roomStatusDisplay}
                  size="small"
                  color={
                    roomStatus === 'completed' ? 'success' : 
                    roomStatus === 'temp_completed' ? 'info' :
                    roomStatus === 'active' ? 'primary' : 
                    roomStatus === 'paused' ? 'warning' :
                    'default'
                  }
                  sx={{ height: 16, fontSize: '0.6rem' }}
                />
                
                {/* WebSocket接続状態 */}
                <Chip
                  icon={connected ? <WifiIcon sx={{ fontSize: 12 }} /> : <WifiOffIcon sx={{ fontSize: 12 }} />}
                  label={connected ? 'リアルタイム' : '接続中...'}
                  size="small"
                  color={connected ? 'success' : 'warning'}
                  sx={{ height: 16, fontSize: '0.6rem' }}
                />
                
                {/* タイピング表示 */}
                {typingUsers.length > 0 && (
                  <Chip
                    icon={<CircleIcon sx={{ fontSize: 8, animation: 'pulse 1.5s infinite' }} />}
                    label={`${typingUsers.length === 1 ? typingUsers[0] : `${typingUsers.length}人`}が入力中`}
                    size="small"
                    color="info"
                    sx={{ height: 16, fontSize: '0.6rem' }}
                  />
                )}
                
                {/* E/Uログインバッジ */}
                {euLoggedIn !== undefined && (
                  <Chip
                    icon={<LoginIcon sx={{ fontSize: '10px !important' }} />}
                    label={euLoggedIn ? 'E/Uログイン済' : 'E/U未ログイン'}
                    size="small"
                    sx={{
                      height: 16, fontSize: '0.6rem', fontWeight: 600,
                      bgcolor: euLoggedIn ? 'success.main' : 'action.disabledBackground',
                      color: euLoggedIn ? '#fff' : 'text.secondary',
                      '& .MuiChip-icon': { color: euLoggedIn ? '#fff' : 'text.secondary' },
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* 強制ロック解除ボタン - 常に表示 */}
          {selectedChatId && (
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={<LockOpenIcon />}
              onClick={handleForceReleaseLock}
              sx={{ mr: 0.5, fontSize: '0.75rem' }}
            >
              強制解除
            </Button>
          )}

          {/* 退室ボタン */}
          {selectedChatId && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<ExitToAppIcon />}
              onClick={handleLeaveRoom}
              sx={{ mr: 1, fontSize: '0.75rem' }}
            >
              退室
            </Button>
          )}

          {/* アクションボタン */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {showCustomerButton && (
              <Button
                size="small"
                startIcon={<InfoIcon />}
                onClick={onShowCustomerProfile}
                sx={{ ml: 1 }}
              >
                顧客情報
              </Button>
            )}

            {/* ステータス選択 */}
            <FormControl size="small" sx={{ ml: 1, minWidth: 120 }}>
              <InputLabel>ステータス</InputLabel>
              <Select
                value={roomStatus || ''}
                onChange={handleStatusChange}
                label="ステータス"
                disabled={!isInRoom}
                sx={{ fontSize: '0.75rem' }}
              >
                <MenuItem value="pending">未対応</MenuItem>
                <MenuItem value="active">対応中</MenuItem>
                <MenuItem value="waiting">返信待ち</MenuItem>
                <MenuItem value="completed">完了</MenuItem>
                <MenuItem value="temp_completed">一時完了</MenuItem>
                <MenuItem value="paused">保留</MenuItem>
                <MenuItem value="archived">削除</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Toolbar>
      </Paper>

      {/* メッセージエリア */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: '#f5f5f5',
          padding: 1,
          paddingBottom: isInRoom ? `${Math.ceil(inputHeight) + 16}px` : '16px', // 入室中のみ入力エリア分のスペース確保
        }}
        ref={messagesContainerRef}
        onClick={scrollToBottom}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Typography color="text.secondary">読み込み中...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Typography color="text.secondary">メッセージがありません</Typography>
          </Box>
        ) : (
          <>
            {messages.map((message, index) => {
              // 運営側（管理者・運営者）は右側、E/Uは左側に表示
              const isCurrentUser = ['admin', 'operator'].includes(message.sender_type);
              const showAvatar = 
                index === 0 || 
                messages[index - 1].sender_id !== message.sender_id ||
                messages[index - 1].sender_type !== message.sender_type;

              return (
                <MessageBubble
                  key={message.id}
                  message={message}
                  isCurrentUser={isCurrentUser}
                  showAvatar={showAvatar}
                  disableEditDelete={roomStatus === 'completed' || roomStatus === 'archived'}
                  onMarkAsRead={async (messageId: string) => {
                    try {
                      if (!selectedChatId) return;
                      const resp = await apiClient.markMessageAsRead(selectedChatId, messageId);
                      if (resp.success) {
                        setMessages(prev => prev.map(m => m.id === messageId ? { ...m, is_read: true } as Message : m));
                      }
                    } catch (e) {
                      setError('既読処理に失敗しました');
                    }
                  }}
                  onEditMessage={handleEditMessage}
                  onDeleteMessage={handleDeleteMessage}
                />
              );
            })}

            {/* タイピングインジケーター */}
            {typingUsers.length > 0 && (
              <Box sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {typingUsers.length === 1 
                    ? `${selectedCustomer?.name || 'お客様'}が入力中...`
                    : `${typingUsers.length}人が入力中...`
                  }
                </Typography>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* 固定入力エリア - 入室中のみ表示 */}
      {isInRoom && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'background.paper',
            border: 'none',
            zIndex: 1100, // オーバーレイ(1000)より高く設定
          }}
          ref={inputContainerRef}
        >
          {/* 全て既読機能は一時停止中のため非表示 */}

          {/* メッセージ入力エリア */}
          <Box sx={{ p: 1 }}>
            <AdminMessageInput
              onSendMessage={handleSendMessage}
              placeholder={
                roomStatus === 'completed' || roomStatus === 'archived'
                  ? 'このチャットは終了しています' 
                  : `${selectedCustomer?.name || 'お客様'}にメッセージを送信 (Shift+Enterで改行)`
              }
              disabled={roomStatus === 'completed' || roomStatus === 'archived'}
              recipientName={selectedCustomer?.name}
              recipientEmail={selectedCustomer?.email}
              recipientPhone={selectedCustomer?.phone}
              enableSendConfirmation={true}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ChatArea;
